import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatBRL, formatDate, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Banknote,
  Building2,
  CalendarDays,
  Hash,
  QrCode,
  RefreshCw,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { formatBankStatusLabel } from "@/lib/labels";

function todayInput() {
  return new Date().toISOString().slice(0, 10);
}

function firstDayInput() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
}

function Metric({ label, value, sub, icon: Icon, testid }) {
  return (
    <Card
      className="rounded-sm border border-zinc-200 shadow-none p-5"
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
          {label}
        </div>
        <Icon className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
      </div>
      <div className="font-display text-3xl font-bold tabular text-zinc-900">
        {value}
      </div>
      {sub && <div className="text-xs text-zinc-500 mt-1 tabular">{sub}</div>}
    </Card>
  );
}

export default function PixRecebidos() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({
    total_qtd: 0,
    total_valor: 0,
    por_banco: [],
  });
  const [emitentes, setEmitentes] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filters, setFilters] = useState({
    emitente_key: "ALL",
    bank_code: "ALL",
    cliente: "",
    data_inicial: firstDayInput(),
    data_final: todayInput(),
  });

  const loadBancos = useCallback(async () => {
    const { data } = await api.get("/bancos");
    setBancos((data || []).filter((b) => b.ativo !== false));
  }, []);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const params = {
          data_inicial: filters.data_inicial || undefined,
          data_final: filters.data_final || undefined,
          configured_only: true,
          limit: 500,
        };
        if (filters.emitente_key !== "ALL")
          params.emitente_key = filters.emitente_key;
        if (filters.bank_code !== "ALL") params.bank_code = filters.bank_code;
        if (filters.cliente.trim()) params.cliente = filters.cliente.trim();

        const { data } = await api.get("/pix/recebidos", { params });
        setItems(data.items || []);
        setTotals({
          total_qtd: data.total_qtd || 0,
          total_valor: data.total_valor || 0,
          por_banco: data.por_banco || [],
        });
        setEmitentes(data.emitentes || []);
      } catch (err) {
        if (!silent)
          toast.error(
            err?.response?.data?.detail || "Erro ao carregar Pix recebidos",
          );
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    loadBancos();
  }, [loadBancos]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const timer = window.setInterval(() => load({ silent: true }), 20000);
    return () => window.clearInterval(timer);
  }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/pix/sincronizar", {
        lookback_minutes: 60,
      });
      if (data.errors) {
        toast.warning(`Consulta Pix concluída com ${data.errors} erro(s)`);
      } else {
        toast.success(
          `Consulta Pix concluída: ${data.received || 0} recebido(s)`,
        );
      }
      await load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Erro ao consultar bancos Pix",
      );
    } finally {
      setSyncing(false);
    }
  };

  const selectedEmitente = useMemo(
    () => emitentes.find((item) => item.emitente_key === filters.emitente_key),
    [emitentes, filters.emitente_key],
  );

  const clear = () => {
    setFilters({
      emitente_key: "ALL",
      bank_code: "ALL",
      cliente: "",
      data_inicial: firstDayInput(),
      data_final: todayInput(),
    });
  };

  return (
    <div className="space-y-6" data-testid="pix-recebidos-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
            Controle MeuPix
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">
            Pix Recebidos
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Transferências Pix recebidas via monitoramento bancário, separadas
            dos pagamentos de boletos.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          className="rounded-sm"
          onClick={syncNow}
          disabled={loading || syncing}
          data-testid="pix-refresh"
        >
          <RefreshCw
            className={`w-4 h-4 mr-2 ${loading || syncing ? "animate-spin" : ""}`}
          />
          Consultar bancos
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric
          label="Quantidade de Pix"
          value={totals.total_qtd}
          icon={Hash}
          testid="pix-total-qtd"
        />
        <Metric
          label="Valor total Pix"
          value={formatBRL(totals.total_valor)}
          icon={Banknote}
          testid="pix-total-valor"
        />
        <Metric
          label="Bancos com Pix"
          value={totals.por_banco.length}
          icon={Building2}
          testid="pix-bancos-qtd"
        />
        <Metric
          label="Emitente selecionado"
          value={filters.emitente_key === "ALL" ? "Todos" : "1"}
          sub={selectedEmitente?.razao_social || "Matriz Pix da Administração"}
          icon={QrCode}
          testid="pix-emitente-kpi"
        />
      </div>

      <Card className="rounded-sm border border-zinc-200 shadow-none p-3 flex flex-wrap gap-2 items-center">
        <Select
          value={filters.emitente_key}
          onValueChange={(v) => setFilters((f) => ({ ...f, emitente_key: v }))}
        >
          <SelectTrigger
            className="w-[240px] h-9 rounded-sm"
            data-testid="pix-filter-emitente"
          >
            <SelectValue placeholder="Emitente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos os emitentes</SelectItem>
            {emitentes.map((emitente) => (
              <SelectItem
                key={emitente.emitente_key}
                value={emitente.emitente_key}
              >
                {emitente.razao_social}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.bank_code}
          onValueChange={(v) => setFilters((f) => ({ ...f, bank_code: v }))}
        >
          <SelectTrigger
            className="w-[190px] h-9 rounded-sm"
            data-testid="pix-filter-banco"
          >
            <SelectValue placeholder="Banco" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos os bancos</SelectItem>
            {bancos.map((banco) => (
              <SelectItem key={banco.id} value={banco.bank_code}>
                {banco.bank_code} - {banco.banco_nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-zinc-400" />
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="date"
              className="w-[145px] h-9 rounded-sm font-mono text-xs"
              value={filters.data_inicial}
              onChange={(e) =>
                setFilters((f) => ({ ...f, data_inicial: e.target.value }))
              }
              data-testid="pix-filter-data-inicial"
            />
            <Input
              type="date"
              className="w-[145px] h-9 rounded-sm font-mono text-xs"
              value={filters.data_final}
              onChange={(e) =>
                setFilters((f) => ({ ...f, data_final: e.target.value }))
              }
              data-testid="pix-filter-data-final"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 min-w-[220px] flex-1">
          <Search className="w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Buscar pagador..."
            className="h-9 rounded-sm"
            value={filters.cliente}
            onChange={(e) =>
              setFilters((f) => ({ ...f, cliente: e.target.value }))
            }
            data-testid="pix-filter-cliente"
          />
        </div>

        <Button
          variant="outline"
          size="sm"
          className="rounded-sm h-9"
          onClick={clear}
          data-testid="pix-filter-clear"
        >
          Limpar
        </Button>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 rounded-sm border border-zinc-200 shadow-none overflow-x-auto">
          <table className="tbl" data-testid="pix-recebidos-table">
            <thead>
              <tr>
                <th>Recebimento</th>
                <th>Pagador</th>
                <th>Emitente</th>
                <th>Banco</th>
                <th>TXID / E2E</th>
                <th className="text-right">Valor</th>
                <th>Status banco</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-12 text-sm text-zinc-500"
                  >
                    Carregando Pix recebidos...
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-12 text-sm text-zinc-500"
                  >
                    Nenhum Pix recebido no filtro atual.
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((pix) => (
                  <tr key={pix.id} data-testid={`pix-row-${pix.id}`}>
                    <td>
                      <div className="font-mono text-xs tabular">
                        {formatDate(pix.data_pagamento || pix.created_at)}
                      </div>
                      <div className="text-[10px] text-zinc-500 font-mono">
                        {formatDateTime(pix.created_at)}
                      </div>
                    </td>
                    <td>
                      <div className="font-medium text-sm">
                        {pix.pagador?.nome || pix.cliente?.nome || "-"}
                      </div>
                      <div className="text-[11px] text-zinc-500 font-mono">
                        {pix.pagador?.cpf_cnpj || pix.cliente?.cpf_cnpj || "-"}
                      </div>
                    </td>
                    <td>
                      <div className="text-sm">
                        {pix.emitente?.razao_social || "-"}
                      </div>
                      <div className="text-[11px] text-zinc-500 font-mono">
                        {pix.emitente?.cnpj || "-"}
                      </div>
                    </td>
                    <td>
                      <Badge
                        variant="outline"
                        className="rounded-sm font-mono text-[10px]"
                      >
                        {pix.bank_code}
                      </Badge>
                      <div className="text-[11px] text-zinc-500 mt-1">
                        {pix.banco_nome}
                      </div>
                    </td>
                    <td
                      className="font-mono text-xs max-w-[220px] truncate"
                      title={pix.txid || pix.nosso_numero}
                    >
                      {pix.txid || pix.nosso_numero || "-"}
                    </td>
                    <td className="text-right font-mono tabular font-semibold">
                      {formatBRL(pix.valor)}
                    </td>
                    <td className="text-xs" title={pix.bank_status || ""}>
                      {formatBankStatusLabel(pix.bank_status)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Card>

        <Card className="rounded-sm border border-zinc-200 shadow-none p-5">
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
              Resumo
            </div>
            <h3 className="font-display text-lg font-semibold">
              Pix por banco
            </h3>
          </div>
          {totals.por_banco.length === 0 ? (
            <div className="text-sm text-zinc-500 py-10 text-center">
              Sem Pix no período.
            </div>
          ) : (
            <div className="space-y-3">
              {totals.por_banco.map((banco) => (
                <div
                  key={banco.bank_code}
                  className="border-b border-zinc-100 pb-3 last:border-0 last:pb-0"
                  data-testid={`pix-bank-${banco.bank_code}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">
                        {banco.banco}
                      </div>
                      <div className="text-[11px] text-zinc-500 font-mono">
                        {banco.bank_code} · {banco.qtd} Pix
                      </div>
                    </div>
                    <div className="font-mono font-semibold tabular text-right">
                      {formatBRL(banco.valor)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
